export function useSubscriptionRoute() {
  const subscriptionFlowRouteName = "subscription";

  const {
    currentRoute,
    rootRoute,
    parseRoute,
    makeGuardResult,
    resolveRouteContext,
  } = useRouteGuard();

  const currentRouteContext = computed(() =>
    resolveSubscriptionRouteContext(currentRoute.value),
  );

  const resolveSubscriptionRouteContext = (route: RouteContextInput) => {
    const result = resolveRouteContext(route, {
      matchPrefixes: {
        subscriptionFlow: subscriptionFlowRouteName,
        paymentFlow: `${subscriptionFlowRouteName}-payment`,
        settingsFlow: "settings",
        welcomeFlow: "welcome",
      },
      queryDefaults: {
        [SESSION_ID_QUERY_PARAM]: undefined as string | undefined,
        subscriptionId: undefined as string | undefined,
      },
      defaults: {
        subscriptionWizardStep: undefined as SubscriptionStep | undefined,
        isInSubscriptionWizardFlow: false,
        requiresActiveSubscription: false,
        outOfSubscriptionFlow: false,
      },
    });

    if (!result.normalizedName) {
      return result;
    }

    result.outOfSubscriptionFlow =
      !result.matches.subscriptionFlow && !result.matches.welcomeFlow;

    // Anything out of settings or allowAnonymous routes (login, signup, etc) should not be accessible without an active subscription
    result.requiresActiveSubscription =
      !result.route.meta.allowAnonymous &&
      !result.matches.subscriptionFlow &&
      !result.matches.settingsFlow &&
      !result.matches.welcomeFlow;

    if (!result.matches.subscriptionFlow) {
      return result;
    }

    const subscriptionSubPageName = result.normalizedName
      .toString()
      .substring(subscriptionFlowRouteName.length + 1);

    const subscriptionWizardStep = subscriptionSteps.find(
      (step) =>
        subscriptionSubPageName.toLowerCase() === step.url.toLowerCase(),
    );

    const isInSubscriptionWizardFlow = subscriptionWizardStep !== undefined;

    return {
      ...result,
      subscriptionWizardStep,
      isInSubscriptionWizardFlow,
    };
  };

  const createSubscriptionPath = (
    options?:
      | {
          error: true;
        }
      | {
          wizardStep: number | SubscriptionStepId | SubscriptionStep;
        },
  ): string => {
    if (options && "error" in options) {
      // TODO: handle error case properly
      return parseRoute("/subscription/error");
    }

    if (!options || !("wizardStep" in options)) {
      return parseRoute({
        name: subscriptionFlowRouteName,
      });
    }

    const SubscriptionStep =
      typeof options.wizardStep === "number"
        ? subscriptionSteps[options.wizardStep]
        : typeof options.wizardStep === "string"
          ? subscriptionSteps.find((step) => step.id === options.wizardStep)
          : options.wizardStep;

    const isValidStep = SubscriptionStep !== undefined;

    if (!isValidStep) {
      devConsole.warn(
        `Invalid subscription step: ${options.wizardStep}. Using default step.`,
      );
      return parseRoute({
        name: subscriptionFlowRouteName,
      });
    }

    return parseRoute({
      name: `${subscriptionFlowRouteName}-${SubscriptionStep.url}`,
    });
  };

  return {
    currentRoute,
    currentRouteContext,
    rootRoute,
    parseRoute,
    makeGuardResult,
    resolveSubscriptionRouteContext,
    createSubscriptionPath,
    routeNames: {
      subscriptionFlowRouteName,
    },
  };
}
